import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PAYOUT_ENDPOINT: Record<string, string> = {
  survivor: 'survivor',
  eliminator: 'eliminator',
  side_pots: 'sidepot',
  custom: 'sidepot',
  high_game: 'highgame',
  high_series: 'highseries',
};

const ordinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

interface Preset {
  placement: number;
  amount: number;
}

interface PayoutPlanItem {
  placement: number;
  amount: number;
  bowler_id: number;
  bowler_name: string;
  pot_label?: string;
}

interface Props {
  sideEvent: { id: number; type: string; name: string };
  leagueId: number;
  seResult: any;
  presetsApiBase: string;
  onPayoutComplete: () => void;
  paidBowlerIds?: Set<number>;
}

// ── Set Payouts Modal ─────────────────────────────────────────────────────────

interface SetPayoutsModalProps {
  presetsApiBase: string;
  initialPresets: Preset[];
  onSave: (presets: Preset[]) => void;
  onClose: () => void;
}

const SetPayoutsModal: React.FC<SetPayoutsModalProps> = ({ presetsApiBase, initialPresets, onSave, onClose }) => {
  const [rows, setRows] = useState<{ placement: string; amount: string }[]>(
    initialPresets.length > 0
      ? initialPresets.map(p => ({ placement: String(p.placement), amount: p.amount.toFixed(2) }))
      : [{ placement: '', amount: '' }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addRow = () => setRows(r => [...r, { placement: '', amount: '' }]);
  const removeRow = (i: number) => setRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i: number, field: 'placement' | 'amount', val: string) =>
    setRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const handleSave = async () => {
    const presets: Preset[] = [];
    for (const row of rows) {
      const p = parseInt(row.placement);
      const a = parseFloat(row.amount);
      if (!isNaN(p) && p > 0 && !isNaN(a) && a > 0) presets.push({ placement: p, amount: a });
    }
    if (presets.length === 0) { setError('Add at least one valid placement with an amount.'); return; }
    const placements = presets.map(p => p.placement);
    if (new Set(placements).size !== placements.length) { setError('Duplicate placements found.'); return; }
    setSaving(true);
    setError(null);
    try {
      await axios.put(`${presetsApiBase}/presets`, { presets }, { headers: authHeader() });
      onSave(presets.sort((a, b) => a.placement - b.placement));
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to save presets.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="bg-gray-800 border border-gray-600 rounded-xl w-full max-w-sm shadow-2xl flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <h3 className="font-bold text-white text-sm">Set Payout Presets</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-lg leading-none">✕</button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          <p className="text-xs text-gray-400 mb-4">
            Define the payout amount for each placement. These amounts are applied when using "Payout with Preset Values".
          </p>
          <div className="space-y-2 mb-4">
            {rows.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 flex-1">
                  <input
                    type="number" min={1} placeholder="Place #"
                    value={row.placement}
                    onChange={e => updateRow(i, 'placement', e.target.value)}
                    className="w-20 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-xs text-white text-center focus:outline-none focus:border-indigo-500" />
                  <span className="text-gray-500 text-xs">→ $</span>
                  <input
                    type="number" min={0.01} step={0.01} placeholder="Amount"
                    value={row.amount}
                    onChange={e => updateRow(i, 'amount', e.target.value)}
                    className="flex-1 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-xs text-white focus:outline-none focus:border-indigo-500" />
                </div>
                <button
                  onClick={() => removeRow(i)}
                  className="text-gray-500 hover:text-red-400 text-sm px-1 transition-colors">
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={addRow}
            className="w-full py-1.5 mb-4 border border-dashed border-gray-600 hover:border-indigo-500 text-gray-400 hover:text-indigo-400 text-xs rounded-lg transition-colors">
            + Add Placement
          </button>
          {error && <p className="text-xs text-red-400 mb-3">{error}</p>}
        </div>
        <div className="px-5 pb-5 flex-shrink-0">
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg font-semibold transition-colors disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Presets'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Confirm Payout Modal ──────────────────────────────────────────────────────

interface ConfirmPayoutModalProps {
  payoutPlan: PayoutPlanItem[];
  leagueId: number;
  sideEventId: number;
  sideEventType: string;
  paidBowlerIds?: Set<number>;
  onComplete: (results: { success: number; failed: number; errors: string[] }) => void;
  onClose: () => void;
}

const ConfirmPayoutModal: React.FC<ConfirmPayoutModalProps> = ({
  payoutPlan, leagueId, sideEventId, sideEventType, paidBowlerIds, onComplete, onClose,
}) => {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<{ success: number; failed: number; errors: string[] } | null>(null);

  const endpoint = PAYOUT_ENDPOINT[sideEventType];

  const execute = async () => {
    if (!endpoint) return;
    setRunning(true);
    let success = 0;
    let failed = 0;
    const errors: string[] = [];
    for (const p of payoutPlan) {
      try {
        await axios.post(
          `${API}/admin/payouts/${endpoint}`,
          {
            sideEventId,
            leagueId,
            recipientType: 'bowler',
            recipientId: p.bowler_id,
            amount: p.amount,
            placement: p.placement,
            notes: `Preset payout – ${ordinal(p.placement)} place${p.pot_label ? ` (${p.pot_label})` : ''}`,
          },
          { headers: authHeader() }
        );
        success++;
      } catch (e: any) {
        failed++;
        errors.push(`${p.bowler_name}: ${e.response?.data?.error || 'failed'}`);
      }
    }
    setRunning(false);
    const r = { success, failed, errors };
    setResults(r);
    onComplete(r);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="bg-gray-800 border border-gray-600 rounded-xl w-full max-w-md shadow-2xl">
        <div className="px-5 py-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="font-bold text-white text-sm">Confirm Preset Payouts</h3>
          {!running && !results && (
            <button onClick={onClose} className="text-gray-400 hover:text-white text-lg leading-none">✕</button>
          )}
        </div>
        <div className="p-5">
          {!results ? (
            <>
              <p className="text-xs text-gray-400 mb-3">The following payouts will be recorded:</p>
              <div className="max-h-60 overflow-y-auto space-y-1 mb-4">
                {payoutPlan.map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-gray-700/50 rounded text-xs">
                    <span className="text-gray-300 flex items-center gap-1.5">
                      {p.pot_label && <span className="text-gray-500">[{p.pot_label}]</span>}
                      <span className="font-semibold text-gray-400">{ordinal(p.placement)}</span>
                      <span>—</span>
                      {p.bowler_name}
                      {paidBowlerIds?.has(p.bowler_id) && (
                        <span className="px-1.5 py-0.5 bg-amber-900/50 text-amber-400 text-[9px] rounded font-bold leading-none">⚠ Already Paid</span>
                      )}
                    </span>
                    <span className="font-bold text-green-400">${p.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <span>{payoutPlan.length} payout{payoutPlan.length !== 1 ? 's' : ''}</span>
                <span className="font-semibold text-white">
                  Total: ${payoutPlan.reduce((s, p) => s + p.amount, 0).toFixed(2)}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors">
                  Cancel
                </button>
                <button
                  onClick={execute}
                  disabled={running}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg font-semibold transition-colors disabled:opacity-50">
                  {running ? 'Paying…' : 'Confirm & Pay All'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={`text-center py-3 mb-4 rounded-lg ${results.failed === 0 ? 'bg-green-900/30 text-green-300' : 'bg-yellow-900/30 text-yellow-300'}`}>
                <div className="font-bold text-sm">
                  {results.success} paid successfully{results.failed > 0 ? `, ${results.failed} failed` : ''}
                </div>
              </div>
              {results.errors.length > 0 && (
                <div className="mb-4 space-y-1">
                  {results.errors.map((e, i) => (
                    <div key={i} className="text-xs text-red-400 bg-red-900/20 px-3 py-1.5 rounded">{e}</div>
                  ))}
                </div>
              )}
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors">
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const SideEventPayoutPresets: React.FC<Props> = ({
  sideEvent, leagueId, seResult, presetsApiBase, onPayoutComplete, paidBowlerIds,
}) => {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [showSetModal, setShowSetModal] = useState(false);
  const [payoutPlan, setPayoutPlan] = useState<PayoutPlanItem[] | null>(null);

  const loadPresets = useCallback(async () => {
    try {
      const r = await axios.get(`${presetsApiBase}/presets`, { headers: authHeader() });
      setPresets(Array.isArray(r.data) ? r.data.map((p: any) => ({ ...p, amount: Number(p.amount) })) : []);
    } catch {
      setPresets([]);
    }
  }, [presetsApiBase]);

  useEffect(() => { loadPresets(); }, [loadPresets]);

  // Not applicable to this event type
  if (!PAYOUT_ENDPOINT[sideEvent.type]) return null;

  const buildPlan = () => {
    if (presets.length === 0) {
      alert('No presets configured. Click "Set Payouts" first.');
      return;
    }

    const plan: PayoutPlanItem[] = [];
    const type = sideEvent.type;

    if (type === 'survivor') {
      const lastRound = seResult?.computed_rounds?.[seResult.computed_rounds.length - 1];
      const lastScores: Record<number, number> = {};
      for (const b of (lastRound?.survivor_list || [])) lastScores[b.bowler_id] = b.score;
      const ranked = [...(seResult?.cashers || [])].sort(
        (a: any, b: any) => (lastScores[b.bowler_id] || 0) - (lastScores[a.bowler_id] || 0)
      );
      for (const preset of presets) {
        const bowler = ranked[preset.placement - 1];
        if (bowler) plan.push({ placement: preset.placement, amount: preset.amount, bowler_id: bowler.bowler_id, bowler_name: bowler.bowler_name });
      }
    } else if (type === 'eliminator') {
      const lastRound = seResult?.computed_rounds?.[seResult.computed_rounds.length - 1];
      const lastScores: Record<number, number> = {};
      for (const b of (lastRound?.advancer_list || [])) lastScores[b.bowler_id] = b.score;
      const ranked = [...(seResult?.cashers || [])].sort(
        (a: any, b: any) => (lastScores[b.bowler_id] || 0) - (lastScores[a.bowler_id] || 0)
      );
      for (const preset of presets) {
        const bowler = ranked[preset.placement - 1];
        if (bowler) plan.push({ placement: preset.placement, amount: preset.amount, bowler_id: bowler.bowler_id, bowler_name: bowler.bowler_name });
      }
    } else if (type === 'high_game') {
      const ranked = [...(seResult?.standings || [])].sort((a: any, b: any) => b.best_game - a.best_game);
      for (const preset of presets) {
        const bowler = ranked[preset.placement - 1];
        if (bowler) plan.push({ placement: preset.placement, amount: preset.amount, bowler_id: bowler.bowler_id, bowler_name: bowler.bowler_name });
      }
    } else if (type === 'high_series') {
      const ranked = [...(seResult?.standings || [])].sort((a: any, b: any) => b.total_score - a.total_score);
      for (const preset of presets) {
        const bowler = ranked[preset.placement - 1];
        if (bowler) plan.push({ placement: preset.placement, amount: preset.amount, bowler_id: bowler.bowler_id, bowler_name: bowler.bowler_name });
      }
    } else if (type === 'side_pots' || type === 'custom') {
      const pots: any[] = seResult?.pot_results || [];
      for (const pot of pots) {
        const potLabel = pot.config_type === 'game_series'
          ? `Series (G${pot.game_identifier})`
          : `Game ${pot.game_identifier}`;
        for (const preset of presets) {
          const entry = (pot.all_entries || []).find((e: any) => e.rank === preset.placement);
          if (!entry) continue;
          if (pot.is_doubles && Array.isArray(entry.bowler_ids) && entry.bowler_ids.length > 0) {
            // Split amount evenly between pair members
            const splitAmount = Math.round((preset.amount / entry.bowler_ids.length) * 100) / 100;
            for (const member of entry.bowler_ids) {
              plan.push({
                placement: preset.placement,
                amount: splitAmount,
                bowler_id: member.bowler_id,
                bowler_name: member.bowler_name,
                pot_label: `${potLabel} (pair)`,
              });
            }
          } else {
            plan.push({
              placement: preset.placement,
              amount: preset.amount,
              bowler_id: entry.bowler_id,
              bowler_name: entry.bowler_name,
              pot_label: potLabel,
            });
          }
        }
      }
    }

    if (plan.length === 0) {
      alert('No matching placements found in current results. Make sure scores have been entered and results are loaded.');
      return;
    }

    setPayoutPlan(plan);
  };

  return (
    <>
      {/* Preset payout action bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 px-3 py-2.5 bg-gray-900/50 border border-gray-700/60 rounded-lg">
        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Preset Payouts</span>
        {presets.length > 0 && (
          <span className="text-[10px] text-gray-500 hidden sm:inline">
            {presets.map(p => `${ordinal(p.placement)}: $${p.amount.toFixed(2)}`).join(' · ')}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowSetModal(true)}
            className="px-2.5 py-1 bg-indigo-800/60 hover:bg-indigo-700 border border-indigo-600/50 text-indigo-300 hover:text-white text-xs rounded-lg flex items-center gap-1.5 transition-colors">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Set Payouts
          </button>
          {presets.length > 0 && (
            <button
              onClick={buildPlan}
              className="px-2.5 py-1 bg-green-700 hover:bg-green-600 text-white text-xs rounded-lg flex items-center gap-1.5 transition-colors font-semibold">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Payout with Preset Values
            </button>
          )}
        </div>
      </div>

      {showSetModal && (
        <SetPayoutsModal
          presetsApiBase={presetsApiBase}
          initialPresets={presets}
          onSave={saved => { setPresets(saved.map(p => ({ ...p, amount: Number(p.amount) }))); setShowSetModal(false); }}
          onClose={() => setShowSetModal(false)}
        />
      )}

      {payoutPlan && (
        <ConfirmPayoutModal
          payoutPlan={payoutPlan}
          leagueId={leagueId}
          sideEventId={sideEvent.id}
          sideEventType={sideEvent.type}
          paidBowlerIds={paidBowlerIds}
          onComplete={r => { if (r.success > 0) onPayoutComplete(); }}
          onClose={() => setPayoutPlan(null)}
        />
      )}
    </>
  );
};

export default SideEventPayoutPresets;
